/* =========================================================
   コレクションページ：スクロール追従「購入はこちら」ボタン

   表示範囲
     ② 商品概要        … インラインボタンのみ（追従は非表示）
     ③ 商品について〜④ 特徴 … 画面下部に固定表示
     ⑤ 商品一覧 以降    … 非表示

   判定はマーカー要素の交差監視（IntersectionObserver）で行う。
   scroll イベントでの都度計算は行わない。
     data-chk-buy-sticky="start" … ③の先頭（Horizonコアの⑤には手を入れられないため
                                    自前セクション側にマーカーを出している）
     data-chk-buy-sticky="end"   … ④の末尾

   表示開始のタイミング
     ③の先頭に達しただけでは、②のインラインボタンがまだ画面に残っていて
     追従ボタンと二重に見えてしまう。そのため「③の先頭に達した」かつ
     「インラインボタンが画面外へ出た」の両方を満たしてから表示する。

   フォールバック
     ③が無い … ②のインラインボタン（data-chk-buy-inline）が画面より上に出たら表示
     ②のボタンが非表示 … ③の先頭に達したら表示
     ④が無い … 非表示にしない（表示したまま）
     いずれも無い … 追従表示しない

   カスタム要素として実装しているため、テーマエディタでセクションが再読み込み
   されると connectedCallback が再実行される。③④は別セクションのため、
   shopify:section:load / unload でも監視対象を取り直す。
   ========================================================= */

(function () {
  if (!window.customElements || customElements.get('chk-buy-sticky')) return;
  if (!('IntersectionObserver' in window)) return;

  customElements.define(
    'chk-buy-sticky',
    class ChkBuySticky extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('a');
        this.observer = null;
        this.started = false;
        this.ended = false;

        if (this.dataset.enabled !== 'true') {
          this.setVisible(false);
          return;
        }

        this.setVisible(false);
        this.refresh();

        // ③④は別セクション。エディタでの追加・削除・並べ替えに追従する。
        this._onSectionChange = this.refresh.bind(this);
        document.addEventListener('shopify:section:load', this._onSectionChange);
        document.addEventListener('shopify:section:unload', this._onSectionChange);
        document.addEventListener('shopify:section:reorder', this._onSectionChange);
      }

      disconnectedCallback() {
        this.disconnectObserver();
        this.restoreInlineFocus();
        document.removeEventListener('shopify:section:load', this._onSectionChange);
        document.removeEventListener('shopify:section:unload', this._onSectionChange);
        document.removeEventListener('shopify:section:reorder', this._onSectionChange);
      }

      disconnectObserver() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
      }

      // 監視対象を取り直す
      refresh() {
        this.disconnectObserver();

        this.startElement = document.querySelector('[data-chk-buy-sticky="start"]');
        this.endElement = document.querySelector('[data-chk-buy-sticky="end"]');
        this.inlineElement = document.querySelector('[data-chk-buy-inline]');

        this.reachedStart = false;
        this.inlineOffScreen = false;
        this.ended = false;

        const targets = [];

        if (this.startElement) targets.push(this.startElement);

        // インラインボタンも常に監視する。
        // ③の先頭に達しただけだと②のインラインボタンがまだ画面に残っていて
        // 追従ボタンと二重に見えてしまうため、インラインボタンが画面外へ
        // 出るまで表示を遅らせる（③が無い場合はこれ単独で開始判定に使う）。
        if (this.inlineElement) targets.push(this.inlineElement);

        if (this.endElement) targets.push(this.endElement);

        // 判定材料が無い場合は追従表示しない
        if (targets.length === 0) {
          this.setVisible(false);
          return;
        }

        this.observer = new IntersectionObserver(this.handleIntersect.bind(this), { threshold: 0 });
        targets.forEach((target) => this.observer.observe(target));
      }

      handleIntersect(entries) {
        entries.forEach((entry) => {
          // 要素が画面の上へ抜けたか（スクロールで通り過ぎたか）
          const passedAbove = entry.boundingClientRect.top < 0;

          if (entry.target === this.startElement) {
            this.reachedStart = entry.isIntersecting || passedAbove;
          } else if (entry.target === this.inlineElement) {
            this.inlineOffScreen = !entry.isIntersecting && passedAbove;
          } else if (entry.target === this.endElement) {
            this.ended = entry.isIntersecting || passedAbove;
          }
        });

        // 表示開始の条件
        //   ③がある場合 … ③の先頭に達し、かつインラインボタンが画面外へ出たあと
        //                （②のボタンと同時に見えないようにするため）
        //   ③が無い場合 … インラインボタンが画面外へ出たら
        //   どちらも無い場合（インラインボタン非表示）… ③の先頭に達したら
        let started;
        if (this.startElement && this.inlineElement) {
          started = this.reachedStart && this.inlineOffScreen;
        } else if (this.inlineElement) {
          started = this.inlineOffScreen;
        } else {
          started = this.reachedStart;
        }

        this.setVisible(started && !this.ended);
      }

      setVisible(visible) {
        if (this.isVisible === visible) return;
        this.isVisible = visible;

        this.classList.toggle('is-visible', visible);

        // 非表示のときはキーボードのフォーカス対象から外す
        if ('inert' in HTMLElement.prototype) {
          this.inert = !visible;
        } else if (this.button) {
          if (visible) {
            this.button.removeAttribute('tabindex');
          } else {
            this.button.setAttribute('tabindex', '-1');
          }
        }

        if (visible) {
          this.removeAttribute('aria-hidden');
        } else {
          this.setAttribute('aria-hidden', 'true');
        }

        // インラインボタンと追従ボタンが同時にフォーカス可能にならないようにする
        this.toggleInlineFocus(visible);
      }

      toggleInlineFocus(stickyVisible) {
        if (!this.inlineElement) return;

        if (stickyVisible) {
          this.inlineElement.setAttribute('tabindex', '-1');
        } else {
          this.inlineElement.removeAttribute('tabindex');
        }
      }

      restoreInlineFocus() {
        if (this.inlineElement) this.inlineElement.removeAttribute('tabindex');
      }
    }
  );
})();
